import Config from '../config/index.js';
import { ActionTypes } from '../config/enums.js';
import { REQUESTED_WITH } from '../shared/constants.js';
import { withTimeout } from '../util/timeout.js';
import { stringify, getEndpointPath, resolve } from '../util/url.js';
import middlewareWrapper from '../util/middleware.js';

/*
 * @forgerock/javascript-sdk
 *
 * index.ts
 *
 * Copyright (c) 2020 ForgeRock. All rights reserved.
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
/**
 * Provides direct access to the OpenAM authentication tree API.
 */
class Auth {
    /**
     * Gets the next step in the authentication tree.
     *
     * @param {Step} previousStep The previous step, including any required input.
     * @param {StepOptions} options Configuration default overrides.
     * @return {Step} The next step in the authentication tree.
     */
    static async next(previousStep, options) {
        const { middleware, realmPath, serverConfig, tree, type } = Config.get(options);
        const query = options ? options.query : {};
        const url = this.constructUrl(serverConfig, realmPath, tree, query);
        const runMiddleware = middlewareWrapper({
            url: new URL(url),
            init: this.configureRequest(previousStep),
        }, {
            type: previousStep ? ActionTypes.Authenticate : ActionTypes.StartAuthenticate,
            payload: {
                tree,
                type: type ? type : 'service',
            },
        });
        const req = runMiddleware(middleware);
        const res = await withTimeout(fetch(req.url.toString(), req.init), serverConfig.timeout);
        const json = await this.getResponseJson(res);
        return json;
    }
    static constructUrl(serverConfig, realmPath, tree, query) {
        const treeParams = tree ? { authIndexType: 'service', authIndexValue: tree } : undefined;
        const params = { ...query, ...treeParams };
        const queryString = Object.keys(params).length > 0 ? `?${stringify(params)}` : '';
        const path = getEndpointPath('authenticate', realmPath, serverConfig.paths);
        const url = resolve(serverConfig.baseUrl, `${path}${queryString}`);
        return url;
    }
    static configureRequest(step) {
        const init = {
            body: step ? JSON.stringify(step) : undefined,
            credentials: 'include',
            headers: new Headers({
                Accept: 'application/json',
                'Accept-API-Version': 'protocol=1.0,resource=2.1',
                'Content-Type': 'application/json',
                'X-Requested-With': REQUESTED_WITH,
            }),
            method: 'POST',
        };
        return init;
    }
    static async getResponseJson(res) {
        const contentType = res.headers.get('content-type');
        const isJson = contentType && contentType.indexOf('application/json') > -1;
        const json = isJson ? await res.json() : {};
        json.status = res.status;
        json.ok = res.ok;
        return json;
    }
}

export { Auth as default };
